import { cn } from '@/lib/utils'
import { HelpCircle, CheckCircle2, ArrowRight } from 'lucide-react'
import { useState } from 'react'

// Define the type locally to avoid build dependency issues
interface AgentChoiceOption {
  id: string;
  label: string;
  description: string;
}

interface AgentChoicePromptProps {
  requestId: string
  question: string
  context: string
  options: AgentChoiceOption[]
  recommendation?: string
  className?: string
  onSelect: (requestId: string, optionId: string, customInput?: string) => void
}

/**
 * AgentChoicePrompt - Displays agent escalation choices for user
 *
 * Used when agent calls ask_user_choice tool to get user guidance:
 * - After multiple failed attempts
 * - When stuck and needs direction
 * - When user preference matters
 *
 * This is a BLOCKING prompt - agent waits for user response
 */
export function AgentChoicePrompt({
  requestId,
  question,
  context,
  options,
  recommendation,
  className,
  onSelect
}: AgentChoicePromptProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSelect = async (optionId: string) => {
    setSelectedOption(optionId)
    setIsSubmitting(true)

    try {
      await onSelect(requestId, optionId)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={cn(
      "rounded-lg border-2 border-amber-500/50 bg-gradient-to-br from-amber-500/5 to-orange-500/5 p-4 animate-fade-in",
      "shadow-lg shadow-amber-500/10",
      className
    )}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 rounded-full bg-amber-500/20">
          <HelpCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">
          Agent needs your input
        </span>
      </div>

      {/* Question */}
      <h3 className="text-base font-medium text-foreground mb-2">
        {question}
      </h3>

      {/* Context */}
      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
        {context}
      </p>

      {/* Options */}
      <div className="space-y-2">
        {options.map((option) => {
          const isRecommended = option.id === recommendation
          const isSelected = option.id === selectedOption

          return (
            <button
              key={option.id}
              onClick={() => handleSelect(option.id)}
              disabled={isSubmitting}
              className={cn(
                "w-full text-left p-3 rounded-lg border-2 transition-all duration-200",
                "hover:shadow-md hover:-translate-y-0.5",
                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none",
                isSelected
                  ? "border-primary bg-primary/10 shadow-md"
                  : isRecommended
                    ? "border-green-500/50 bg-green-500/5 hover:border-green-500 hover:bg-green-500/10"
                    : "border-border hover:border-primary/50 hover:bg-muted/50"
              )}
            >
              <div className="flex items-center gap-3">
                {/* Selection indicator */}
                <div className={cn(
                  "w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors",
                  isSelected
                    ? "border-primary bg-primary"
                    : "border-muted-foreground/30"
                )}>
                  {isSelected && <CheckCircle2 className="h-3 w-3 text-primary-foreground" />}
                </div>

                {/* Option content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">
                      {option.label}
                    </span>
                    {isRecommended && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-700 dark:text-green-400 font-medium">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {option.description}
                  </p>
                </div>

                {/* Arrow */}
                <ArrowRight className={cn(
                  "h-4 w-4 flex-shrink-0 transition-transform",
                  isSelected ? "text-primary translate-x-1" : "text-muted-foreground/50"
                )} />
              </div>
            </button>
          )
        })}
      </div>

      {/* Loading indicator */}
      {isSubmitting && (
        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span>Processing your choice...</span>
        </div>
      )}
    </div>
  )
}
